/**
 * TrustBar - Floating trust signals bar visible on all public pages.
 * Shows verifiable trust signals only (compliance + support + phone).
 * Unverified star ratings / review counts removed for integrity.
 */
import { forwardRef } from 'react';
import { Shield, Clock, Phone } from 'lucide-react';

const trustBarContent: Record<string, { gdpr: string; support: string }> = {
  en: { gdpr: 'Data protection', support: '24/7 support on request' },
  fr: { gdpr: 'Protection des données', support: 'Support 24/7 sur demande' },
  es: { gdpr: 'Protección de datos', support: 'Soporte 24/7 a petición' },
  ar: { gdpr: 'حماية البيانات', support: 'دعم 24/7 عند الطلب' },
  de: { gdpr: 'Datenschutz', support: '24/7-Support auf Anfrage' },
  it: { gdpr: 'Protezione dei dati', support: 'Supporto 24/7 su richiesta' },
  pt: { gdpr: 'Proteção de dados', support: 'Suporte 24/7 mediante pedido' },
  nl: { gdpr: 'Gegevensbescherming', support: '24/7 ondersteuning op aanvraag' },
  pl: { gdpr: 'Ochrona danych', support: 'Wsparcie 24/7' },
  ru: { gdpr: 'Защита данных', support: 'Поддержка 24/7' },
  zh: { gdpr: '数据保护', support: '可按需安排 24/7 服务' },
  ja: { gdpr: 'データ保護', support: '24時間対応はご要望に応じて' },
  tr: { gdpr: 'Veri koruma', support: '7/24 Destek' },
  ko: { gdpr: '데이터 보호', support: '24/7 운영은 요청 시 협의' },
  sv: { gdpr: 'Dataskydd', support: 'Support dygnet runt' },
  da: { gdpr: 'Databeskyttelse', support: 'Døgnsupport' },
  th: { gdpr: 'การคุ้มครองข้อมูล', support: 'สนับสนุน 24/7' },
  vi: { gdpr: 'Bảo vệ dữ liệu', support: 'Hỗ trợ 24/7' },
  el: { gdpr: 'Προστασία δεδομένων', support: 'Υποστήριξη 24/7' },
  no: { gdpr: 'Databeskyttelse', support: 'Døgnstøtte' },
  hi: { gdpr: 'डेटा सुरक्षा', support: '24/7 सहायता' },
  fi: { gdpr: 'Tietosuoja', support: '24/7 Tuki' },
  hu: { gdpr: 'Adatvédelem', support: '24/7 Támogatás' },
  cs: { gdpr: 'Ochrana dat', support: '24/7 Podpora' },
  ro: { gdpr: 'Protecția datelor', support: 'Suport 24/7' },
  id: { gdpr: 'Perlindungan data', support: 'Dukungan 24/7' },
  ms: { gdpr: 'Perlindungan data', support: 'Sokongan 24/7' },
  bn: { gdpr: 'তথ্য সুরক্ষা', support: '24/7 সহায়তা' },
  tl: { gdpr: 'Data protection', support: '24/7 Suporta' },
};

export const TrustBar = forwardRef<HTMLDivElement, { language?: string }>(function TrustBar({ language = 'en' }, ref) {
  const t = trustBarContent[language] || trustBarContent.en;
  return (
    <div ref={ref} className="hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-muted/20/95 backdrop-blur-md border-t border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-8 py-2 text-xs text-primary-foreground/90">
          <span className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-teal" />
            {t.gdpr}
          </span>
                  <span className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-teal" />
            {t.support}
          </span>
          <a
            href="tel:+212 5 37 37 37 77"
            className="flex items-center gap-1.5 text-primary-foreground/90 hover:text-primary-foreground transition-colors"
          >
            <Phone className="h-3.5 w-3.5 text-coral" />
            +212 5 37 37 37 77
          </a>
        </div>
      </div>
    </div>
  );
});

TrustBar.displayName = 'TrustBar';
